import { flexRender } from "@tanstack/react-table";
import type { Table } from "@tanstack/react-table";
import type { Profile } from "@/services/api/companies";

interface TableContentProps {
  table: Table<Profile>;
  isLoading: boolean;
  profiles: Profile[];
}

export function TableContent({
  table,
  isLoading,
  profiles,
}: TableContentProps) {
  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">Loading...</div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">
        No company staff found.
      </div>
    );
  }

  return (
    <table
      className="border-collapse w-full"
      style={{ width: table.getTotalSize() }}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            className="border-b border-grid-200 bg-white sticky top-0 z-10"
          >
            {headerGroup.headers.map((header) => {
              if (!header.column.getIsVisible()) {
                return null;
              }
              return (
                <th
                  key={header.id}
                  className="relative px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-600 whitespace-nowrap"
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {header.column.getCanResize() && (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`absolute right-0 top-0 h-full w-3 cursor-col-resize select-none touch-none ${header.column.getIsResizing() ? "bg-grid-200" : ""}`}
                      aria-label="Resize column"
                    />
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            className="border-b border-grid-100 hover:bg-grid-50 transition-colors"
          >
            {row.getVisibleCells().map((cell) => {
              if (!cell.column.getIsVisible()) {
                return null;
              }
              return (
                <td
                  key={cell.id}
                  className="px-4 py-3 whitespace-nowrap"
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
